import { InputHTMLAttributes, ReactNode, forwardRef, useId } from 'react';

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Mensagem de erro exibida abaixo do campo. */
  error?: string;
  /** Texto de ajuda (dica) exibido abaixo do campo quando não há erro. */
  hint?: string;
  /** Ícone FontAwesome à esquerda (ex.: 'fa-envelope'). */
  icon?: string;
  /** Conteúdo à direita do input (ex.: botão de olho da senha). */
  trailing?: ReactNode;
}

/**
 * Campo de formulário acessível reutilizado por todos os passos do wizard.
 * Rótulo sempre visível, `aria-invalid` e `aria-describedby` corretos.
 */
export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  { label, error, hint, icon, trailing, id, className = '', required, ...inputProps },
  ref,
) {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const describedById = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

  return (
    <div className="mb-4">
      <label htmlFor={fieldId} className="block mb-[6px] text-[12px] font-semibold text-navy-800">
        {label}
        {required && <span className="text-alert-caution ml-[2px]" aria-hidden="true">*</span>}
      </label>
      <div
        className={`flex items-center bg-white rounded-[20px] h-[42px] overflow-hidden border border-solid transition-colors ${
          error
            ? 'border-alert-caution focus-within:ring-2 focus-within:ring-alert-caution/30'
            : 'border-mist-300 focus-within:ring-2 focus-within:ring-sky-500/30 focus-within:border-sky-500'
        }`}
      >
        {icon && (
          <i className={`fa ${icon} pl-[15px] text-[14px] text-mist-500`} aria-hidden="true"></i>
        )}
        <input
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={!!error}
          aria-describedby={describedById}
          className={`flex-1 h-full px-[15px] border-0 text-[13px] text-navy-800 bg-transparent focus:outline-none placeholder:text-mist-500 ${className}`}
          {...inputProps}
        />
        {trailing}
      </div>
      {error ? (
        <p id={`${fieldId}-error`} className="mt-[5px] text-[11px] text-alert-caution flex items-center gap-1">
          <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
          {error}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className="mt-[5px] text-[11px] text-navy-700/70">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
